import Bugsnag from '@bugsnag/js';
import {
  MutationCache,
  MutationKey,
  QueryCache,
  QueryClient,
  QueryKey,
} from '@tanstack/react-query';
import { callEvent } from '../Amplitude/AmplitudeUtilities';
import { AUTH_ERROR_MESSAGES } from './authErrors';
import { redactBreadcrumbVariables } from './breadcrumbVariables';

type QueryErrorResponse = {
  errors?: Array<{
    message?: string;
    errorType?: string;
  }>;
};

type QueryError = {
  response?: QueryErrorResponse;
};

const getQueryErrorMessage = (error: unknown) => {
  if (error instanceof Error) {
    return error.message;
  }

  return (
    (error as QueryError)?.response?.errors?.[0]?.message ?? 'Unknown error'
  );
};

const getQueryKeyLabel = (
  contextType: 'Query' | 'Mutation',
  queryKey?: QueryKey | MutationKey
) => {
  if (Array.isArray(queryKey)) {
    const keyString = queryKey.map(value => String(value)).join('.');
    return `${contextType}: ${keyString}`;
  }

  return `Unknown ${contextType}`;
};

const reportRequestError = (
  error: unknown,
  contextType: 'Query' | 'Mutation',
  key?: QueryKey | MutationKey,
  variables?: unknown
) => {
  const payload = {
    error: getQueryErrorMessage(error),
    errorOperation: getQueryKeyLabel(contextType, key),
  };

  try {
    callEvent('api query error', payload);
    // Variables carry comments, form field values and File attachments, so
    // only their redacted shape is safe to send.
    const redactedVariables = redactBreadcrumbVariables(variables);
    Bugsnag.leaveBreadcrumb(
      `API ${contextType} Failed`,
      {
        ...payload,
        ...(redactedVariables === undefined
          ? {}
          : { variables: redactedVariables }),
      },
      'error'
    );
  } catch (reportingError) {
    console.error('Failed to report query error', reportingError);
  }
};

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // Terminal auth errors (missing/expired token -> signing out) are never
        // retried.
        if (AUTH_ERROR_MESSAGES.includes((error as Error)?.message ?? '')) {
          return false;
        }
        // A surfaced UnauthorizedException (token still valid, server rejected)
        // is shown to the user, not retried.
        const errorType = (error as QueryError)?.response?.errors?.[0]
          ?.errorType;
        if (errorType === 'UnauthorizedException') {
          return false;
        }
        return failureCount < 2;
      },
    },
  },
  queryCache: new QueryCache({
    onError: (error, query) => {
      // Avoid spamming BugSnag during automatic background retries
      if (query.state.data !== undefined) return;

      reportRequestError(error, 'Query', query.queryKey);
    },
  }),
  mutationCache: new MutationCache({
    onError: (error, variables, context, mutation) => {
      reportRequestError(
        error,
        'Mutation',
        mutation.options.mutationKey,
        variables
      );
    },
  }),
});
